import {
  Layers, Type, Image, Megaphone,
  Zap, Users, Star, Calendar,
  Minus, ArrowUpDown, Lock, LockOpen,
  Trash2, Copy, GripVertical,
} from 'lucide-react'
import type { CanvasBlock } from '@/lib/cms/card-registry'

/* ── Props ───────────────────────────────────────────── */

interface LayersPanelProps {
  blocks: CanvasBlock[]
  selectedBlockId: string | null
  lockedBlockIds: Set<string>
  onSelect: (id: string | null) => void
  onRemove: (id: string) => void
  onDuplicate: (id: string) => void
  onReorder: (blocks: CanvasBlock[]) => void
  onToggleLock: (id: string) => void
}

/* ── Block type icon mapping ─────────────────────────── */

function getBlockIcon(type: string) {
  switch (type) {
    case 'hero-banner': return <Megaphone size={12} />
    case 'text-block': case 'heading': return <Type size={12} />
    case 'image-block': case 'gallery-image': return <Image size={12} />
    case 'cta-block': return <Zap size={12} />
    case 'service-card': return <Star size={12} />
    case 'team-card': return <Users size={12} />
    case 'testimonial-card': return <Star size={12} />
    case 'event-card': return <Calendar size={12} />
    case 'spacer': return <ArrowUpDown size={12} />
    case 'divider': return <Minus size={12} />
    default: return <Layers size={12} />
  }
}

function getBlockDisplayName(block: CanvasBlock): string {
  return block.data?.headline || block.data?.heading || block.data?.name || block.data?.title || block.data?.text || block.type.replace(/-/g, ' ')
}

function getBlockTypeBadge(type: string): string {
  return type.replace(/-/g, ' ').split(' ').map(w => w[0]?.toUpperCase()).join('')
}

/* ── Component ───────────────────────────────────────── */

export default function LayersPanel({
  blocks,
  selectedBlockId,
  lockedBlockIds,
  onSelect,
  onRemove,
  onDuplicate,
  onToggleLock,
}: LayersPanelProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Section header */}
      <div className="editor-section-header" style={{ cursor: 'default' }}>
        <Layers size={12} />
        <span style={{ flex: 1 }}>DOCUMENT STRUCTURE</span>
        <span className="editor-badge">{blocks.length}</span>
      </div>

      {/* Tree view */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '2px 0' }}>
        {blocks.length === 0 && (
          <div className="editor-empty-state">
            <Layers size={24} className="editor-empty-state-icon" />
            <div className="editor-empty-state-text">No blocks yet</div>
            <div className="editor-empty-state-hint">Add blocks from the Components tab</div>
          </div>
        )}

        {blocks.map((block) => {
          const isSelected = block.id === selectedBlockId
          const isLocked = lockedBlockIds.has(block.id)

          return (
            <div
              key={block.id}
              className="editor-tree-row"
              data-selected={isSelected}
              data-locked={isLocked}
              onClick={() => onSelect(block.id)}
              data-testid={`layer-${block.id}`}
            >
              {/* Drag handle */}
              <GripVertical size={10} style={{ color: 'var(--editor-text-dim)', cursor: 'grab', flexShrink: 0 }} />

              {/* Type icon */}
              <span className="editor-tree-icon">
                {getBlockIcon(block.type)}
              </span>

              {/* Label */}
              <span className="editor-tree-label">
                {getBlockDisplayName(block)}
              </span>

              {/* Type badge */}
              <span className="editor-tree-type">{getBlockTypeBadge(block.type)}</span>

              {/* Actions (visible on hover) */}
              <div className="editor-tree-actions">
                <button
                  className="editor-btn editor-btn-ghost editor-btn-icon editor-btn-sm"
                  onClick={e => { e.stopPropagation(); onToggleLock(block.id) }}
                  title={isLocked ? 'Unlock' : 'Lock'}
                >
                  {isLocked ? <Lock size={10} /> : <LockOpen size={10} />}
                </button>
                <button
                  className="editor-btn editor-btn-ghost editor-btn-icon editor-btn-sm"
                  onClick={e => { e.stopPropagation(); onDuplicate(block.id) }}
                  title="Duplicate"
                  disabled={isLocked}
                >
                  <Copy size={10} />
                </button>
                <button
                  className="editor-btn editor-btn-ghost editor-btn-icon editor-btn-sm"
                  onClick={e => { e.stopPropagation(); onRemove(block.id) }}
                  title="Delete"
                  disabled={isLocked}
                  style={{ color: isLocked ? undefined : 'var(--editor-danger)' }}
                >
                  <Trash2 size={10} />
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
